import React from 'react';
import { BarChart, XAxis, YAxis, Legend, CartesianGrid, Bar, ResponsiveContainer, Tooltip } from 'recharts';

import { makeStyles, useTheme, darken } from '@material-ui/core/styles';

import { Criterium, CriteriumSkeleton } from '@hydra/common/criterium';
import { useReviewContext } from '@hydra/common/context';
import CriteriumChart from '@hydra/common/criterium/CriteriumChart/CriteriumChart';
import { Dot, Translate } from '@hydra/common/components';

import hasCritData from '@hydra/utils/hasCritData';

import { useI18n } from '@hydra/common/customHooks';

import { CriteriumInterface, ReviewData } from '@hydra/types/review';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[200]}`,
    display: 'block',
    padding: theme.spacing(2),
    borderSpacing: '5px',
    borderCollapse: 'separate'
  },
  th: { textAlign: 'center' },
  left: { textAlign: 'left' },
  right: { textAlign: 'right' },
  legendSpan: {
    marginRight: theme.spacing(0.75),
    color: darken(theme.palette.grey[900], 0.3)
  }
}));

const CHART_OPTIONS = {
  width: '99%',
  height: 300
};

interface CustomTooltipProps {
  payload?: {
    name: string
    value: string
    color: string
  }[]
  label?: string
  formatter?: (value: string) => string
  labelFormatter?: (label?: string) => string
}

type TrafficSourcesKeys = 'search' | 'social' | 'mail' | 'direct' | 'referrals' | 'paidReferrals';

interface TrafficSources extends CriteriumInterface {
  data: {
    [id in TrafficSourcesKeys]: number
  }
}

function adjustData (data: {
  [id in TrafficSourcesKeys]: number
}): {
    [id in TrafficSourcesKeys]: number
  } {
  const result = {
    search: 0,
    social: 0,
    mail: 0,
    direct: 0,
    referrals: 0,
    paidReferrals: 0
  };
  let sum = 0;

  const keys = Object.keys(data) as Array<TrafficSourcesKeys>;
  keys.forEach((key: TrafficSourcesKeys) => {
    const number = Math.round(data[key] * 100) / 100;
    sum += number;
    result[key] = number;
  });

  const difference = Math.round((1 - sum) * 100) / 100;
  if (difference !== 0) {
    const sourceWithMinValue = keys.map((key: TrafficSourcesKeys) => {
      return { key, value: data[key as TrafficSourcesKeys] };
    }).sort((a, b) => {
      if (a.value < b.value) { return -1; }
      if (a.value > b.value) { return 1; }
      return 0;
    }).find(data => data.value !== 0);

    if (sourceWithMinValue) {
      result[sourceWithMinValue.key as TrafficSourcesKeys] += difference;
    }
  }
  return result;
}

function CustomTooltip ({ payload = [], label = '', labelFormatter = () => '', formatter }: CustomTooltipProps) {
  const classes = useStyles();

  return (
    <table className={classes.root}>
      <thead>
        <tr>
          <th className={classes.th} colSpan={3}>{labelFormatter(label)}</th>
        </tr>
      </thead>
      <tbody>
        {payload.map(({ name, value, color }) => {
          return (
            <tr key={name}>
              <td className={classes.left}>
                <Dot size='sm' color={color} />
              </td>
              <td className={classes.left}>
                <Translate content={`traffic_sources_${name.toLowerCase()}`} />
              </td>
              <td className={classes.right}>
                {formatter ? formatter(value) : value}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function StackedBarChart ({ data, sortedData, colors }: { data: any, sortedData: any, colors: string[] }) {
  const { numeral } = useI18n();
  const { variant } = useReviewContext();
  const classes = useStyles();

  if (variant === 'nocrit') {
    return <CriteriumSkeleton variant='rect' style={{ height: '150px' }} />;
  }

  const labelFormatter = (label: string) => {
    return label;
  };

  const formatter = (value: number) => {
    return numeral(value / 100).format('0%');
  };

  const tickFormatter = (value: number) => `${value}%`;

  const legendFormatter = (value: string) => {
    return (
      <span className={classes.legendSpan}>
        <Translate content={`traffic_sources_${value.toLowerCase()}`} />
      </span>
    );
  };

  return (
    // using width 99% to make container resize properly https://github.com/recharts/recharts/issues/172#issuecomment-307858843
    <ResponsiveContainer {...CHART_OPTIONS}>
      <BarChart data={data} margin={{ top: 20 }}>
        <XAxis
          axisLine={false}
          tickLine={false}
          dataKey='url'
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ transform: 'translate(-5, 0)' }}
          domain={[0, 100]}
          tickFormatter={tickFormatter}
        />
        <CartesianGrid vertical={false} />
        <Legend wrapperStyle={{ bottom: -10 }} formatter={legendFormatter} />
        <Tooltip
          formatter={formatter}
          labelFormatter={labelFormatter}
          content={<CustomTooltip />}
          cursor={false}
        />
        {
          sortedData.map(({ source }: { source: string }, i: number) => (
            <Bar
              key={i}
              dataKey={source}
              stackId='a'
              fill={colors[i % colors.length]}
              barSize={30}
            />
          ))
        }
      </BarChart>
    </ResponsiveContainer>
  );
}

export const TrafficSources = ({ criterium, competitors = [] }: { criterium: TrafficSources, competitors: Array<ReviewData> }) => {
  const { translate } = useI18n();
  const theme = useTheme();

  if (Object.keys(criterium.data).length === 0) {
    return null;
  }

  const colors = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.competitor.main,
    theme.palette.competitor.dark
  ];

  const adjustedData = adjustData(criterium.data);
  const data = [
    { name: translate('traffic_sources_search'), value: adjustedData.search * 100, source: 'search' },
    { name: translate('traffic_sources_social'), value: adjustedData.social * 100, source: 'social' },
    { name: translate('traffic_sources_mail'), value: adjustedData.mail * 100, source: 'mail' },
    { name: translate('traffic_sources_direct'), value: adjustedData.direct * 100, source: 'direct' },
    { name: translate('traffic_sources_referrals'), value: adjustedData.referrals * 100, source: 'referrals' },
    { name: translate('traffic_sources_paidreferrals'), value: adjustedData.paidReferrals * 100, source: 'paidReferrals' }
  ];

  const sortedData = data
    .sort((a, b) => {
      if (a.value < b.value) { return 1; }
      if (a.value > b.value) { return -1; }
      return 0;
    });

  const chartProps = {
    data: {
      name: 'traffic_sources',
      data: sortedData
    }
  };

  const competitorData = [{
    url: criterium.url,
    search: adjustedData.search * 100,
    social: adjustedData.social * 100,
    mail: adjustedData.mail * 100,
    direct: adjustedData.direct * 100,
    referrals: adjustedData.referrals * 100,
    paidReferrals: adjustedData.paidReferrals * 100
  }];

  competitors.forEach((competitor) => {
    if (competitor.criteria && hasCritData(competitor.criteria.traffic_sources)) {
      const { url, data } = competitor.criteria.traffic_sources;
      const adjustedData = adjustData(data);

      competitorData.push({
        url,
        search: adjustedData.search * 100,
        social: adjustedData.social * 100,
        mail: adjustedData.mail * 100,
        direct: adjustedData.direct * 100,
        referrals: adjustedData.referrals * 100,
        paidReferrals: adjustedData.paidReferrals * 100
      });
    }
  });

  const content = competitorData.length > 1
    ? <StackedBarChart data={competitorData} sortedData={sortedData} colors={colors} />
    : <CriteriumChart type='pie' {...chartProps} colors={colors} />;

  return <Criterium criterium={criterium}>{content}</Criterium>;
};

export default React.memo(TrafficSources);
